import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from "react-redux";
import Button from "@material-ui/core/Button";
import { Save as SaveIcon, PictureAsPdf as PdfIcon, Visibility as VisibilityIcon } from "@material-ui/icons";
import LoadingOverlay from "../LoadingOverlay";
import Header from "../Header/Header";
import NumsDoss from "../NumsDoss";
import DocMenu from "../DocMenu";
import axios from "axios";
import { changeTitleName, changePdfFileName, changeIndication } from "../../redux/actions/emailAction";
import { getDossierInfos } from '../Utils';
import "../../Styles/CISSCT/InternalRules.scss";
import GenPdf from "../GenPdf";

const InternalRules = () => {
  const dispatch = useDispatch();
  const dossierValue = useSelector((state) => state.devis.dossierValue);

  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [disableButton, setDisableButton] = useState(false);

  // État du formulaire avec tous les champs variables
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    operation: '',
    address: '',
    projectOwner: '',
    spsCoordinator: 'COORDOGEC',
    meetingPlace: '',
    meetingFrequency: 'une fois par trimestre',
    noticePeriod: '15 jours',
    archivingPeriod: '5 ans'
  });

  const [errors, setErrors] = useState({});


  const fetchData = async () => {
    if (!dossierValue) return;
    setIsLoading(true);

    try {
      const result = await getDossierInfos(dossierValue);
      setFormData({
        date: new Date().toISOString().slice(0, 10),
        operation: result.operation,
        address: `${result.adresse_op}, ${result.cp_op} - ${result.ville_op}`,
        projectOwner: result.sci_sccv,
        spsCoordinator: 'COORDOGEC',
        meetingPlace: `${result.adresse_op}, ${result.cp_op} - ${result.ville_op}`,
        meetingFrequency: 'une fois par trimestre',
        noticePeriod: '15 jours',
        archivingPeriod: '5 ans'
      });

    } catch (error) {
      console.error("Erreur dans useEffect:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleSaveClick = async () => {
    setIsLoading(true);
    try {
      const requestOptions = {
        ndossier: dossierValue,
        data: JSON.stringify(formData)
      };

      const response = await axios.post('/api/cissct/saveInternalRules', requestOptions, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status !== 200) {
        throw new Error("Erreur lors de l'enregistrement du CISSCT Règlement intérieur");
      }
    } catch (error) {
      console.error("Erreur lors de l'enregistrement du CISSCT Règlement intérieur:", error);
    } finally {
      setIsLoading(false);
    }
  }

  const handleGetInertialRules = async () => {
    if (!dossierValue) return;
    setIsLoading(true);

    try {
      const response = await axios.get(`/api/cissct/getInternalRules/${dossierValue}`);

      const parsed = JSON.parse(response.data.data);
      setFormData(parsed);

    } catch (error) {
      if (error.response) {
        const status = error.response.status;

        if (status === 404) {
          fetchData();
          return;
        }

        console.error("Erreur API:", error.response.data?.error || error.message);
      } else {
        console.error("Erreur réseau:", error.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGetInertialRules();
  }, [dossierValue]);

  const handleGenClick = async () => {
    setIsLoading(true);
    setFile(null);

    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        ndossier: dossierValue,
        data: formData
      }),
    };
    let blob = await fetch("/api/cissct/genInternalRulesPdf", requestOptions)
      .then((res) => res.blob())
      .catch((err) => {
        console.log(err);
      });
    if (blob) {
      setFile(blob);
      setIsOpen(true);
    }
    setIsLoading(false);
  }

  const handleShowPdf = useCallback(async () => {
    setIsLoading(true);
    setFile(null);
    try {
      const response = await fetch(`/api/cissct/getInternalRulesPdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          type: 'pdf',
        }),
      });
      const blob = await response.blob();
      if (blob.size > 100) {
        const fileName = response.headers.get('Content-Disposition').split('filename=')[1];
        dispatch(changePdfFileName(fileName));
        setFile(blob);
        setIsOpen(true);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, [dossierValue]);

  const handleGetWordFile = async () => {
    setIsLoading(true);
    setFile(null);
    try {
      const response = await fetch(`/api/cissct/getInternalRulesPdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          type: 'word',
        }),
      });
      const blob = await response.blob();
      if (blob.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const fileName = response.headers.get('Content-Disposition').split('filename=')[1];
        dispatch(changePdfFileName(fileName));
        setFile(blob);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  const handleGetPdfFile = async () => {
    setIsLoading(true);
    setFile(null);
    try {
      const response = await fetch(`/api/cissct/getInternalRulesPdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ndossier: dossierValue,
          type: 'pdf',
        }),
      });
      const blob = await response.blob();
      if (blob.size > 100) {
        const fileName = response.headers.get('Content-Disposition').split('filename=')[1];
        dispatch(changePdfFileName(fileName));
        setFile(blob);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }

  const handleChangeTitleName = () => {
    dispatch(changeTitleName(`Règlement intérieur`));
  };

  return (
    <div className="cissct-internal-rules">
      {isLoading && <LoadingOverlay />}
      <Header />
      <DocMenu
        file={file}
        getSourceFile={handleGetWordFile}
        getPDF={handleGetPdfFile}
        handleChangeTitleName={handleChangeTitleName}
        updateIndication={() => dispatch(changeIndication('Règlement intérieur'))}
      />
      <h1 className="title">Règlement intérieur du CISSCT</h1>
      <NumsDoss />

      <div className="container">
        <div className="form-content">
          <div className="form-section">
            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="operation">Date *</label>
                <input
                  id="date"
                  type="date"
                  style={{ width: '150px' }}
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                />
              </div>
            </div>
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
              </svg>
              Informations du Chantier
            </h2>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="operation">Dossier *</label>
                <input
                  id="dossier"
                  value={dossierValue ? dossierValue?.toString().split('').join('-') : ''}
                  rows="2"
                  readOnly
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="operation">Opération *</label>
                <input
                  id="operation"
                  value={formData.operation}
                  onChange={(e) => handleInputChange('operation', e.target.value)}
                  rows="2"
                  placeholder="Ex: CONSTRUCTION DE 68 LOGEMENTS À USAGE D'HABITATION..."
                  className={errors.operation ? 'error' : ''}
                />
                {errors.operation && <span className="error-message">{errors.operation}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="adresse">Adresse *</label>
                <input
                  type="text"
                  id="adresse"
                  value={formData.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  placeholder="Ex: 123 Rue de la République, 75001 - Paris"
                  className={errors.address ? 'error' : ''}
                />
                {errors.address && <span className="error-message">{errors.address}</span>}
              </div>
            </div>
          </div>

          {/* Section Acteurs du Projet */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M7 14s-1 0-1-1 1-4 5-4 5 3 5 4-1 1-1 1H7zm4-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                <path fillRule="evenodd" d="M5.216 14A2.238 2.238 0 0 1 5 13c0-1.355.68-2.75 1.936-3.72A6.325 6.325 0 0 0 5 9c-4 0-5 3-5 4s1 1 1 1h4.216z" />
                <path d="M4.5 8a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5z" />
              </svg>
              Acteurs du Projet
            </h2>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="projectOwner">Maître d'Ouvrage *</label>
                <input
                  type="text"
                  id="projectOwner"
                  value={formData.projectOwner}
                  onChange={(e) => handleInputChange('projectOwner', e.target.value)}
                  placeholder="Ex: SCCV EMERAINVILLE DU CLOS"
                  className={errors.projectOwner ? 'error' : ''}
                />
                {errors.projectOwner && <span className="error-message">{errors.projectOwner}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="spsCoordinator">Coordonnateur SPS *</label>
                <input
                  type="text"
                  id="spsCoordinator"
                  value={formData.spsCoordinator}
                  onChange={(e) => handleInputChange('spsCoordinator', e.target.value)}
                  placeholder="Ex: COORDOGEC"
                  className={errors.spsCoordinator ? 'error' : ''}
                />
                {errors.spsCoordinator && <span className="error-message">{errors.spsCoordinator}</span>}
              </div>
            </div>
          </div>

          {/* Section Organisation des Réunions */}
          <div className="form-section">
            <h2 className="section-title">
              <svg width="20" height="20" viewBox="0 0 16 16" fill="currentColor">
                <path d="M3.5 0a.5.5 0 0 1 .5.5V1h8V.5a.5.5 0 0 1 1 0V1h1a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2V3a2 2 0 0 1 2-2h1V.5a.5.5 0 0 1 .5-.5zM1 4v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V4H1z" />
              </svg>
              Organisation des Réunions
            </h2>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="meetingPlace">Lieu des réunions *</label>
                <input
                  type="text"
                  id="meetingPlace"
                  value={formData.meetingPlace}
                  onChange={(e) => handleInputChange('meetingPlace', e.target.value)}
                  placeholder="Ex: 123 Rue de la République, 75001 - Paris"
                  className={errors.meetingPlace ? 'error' : ''}
                />
                {errors.meetingPlace && <span className="error-message">{errors.meetingPlace}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="meetingFrequency">Fréquence des réunions</label>
                <select
                  id="meetingFrequency"
                  value={formData.meetingFrequency}
                  onChange={(e) => handleInputChange('meetingFrequency', e.target.value)}
                >
                  <option value="une fois par mois">Une fois par mois</option>
                  <option value="une fois par trimestre">Une fois par trimestre</option>
                  <option value="une fois tous les deux mois">Une fois tous les deux mois</option>
                  <option value="selon les besoins">Selon les besoins</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="noticePeriod">Délai de convocation</label>
                <select
                  id="noticePeriod"
                  value={formData.noticePeriod}
                  onChange={(e) => handleInputChange('noticePeriod', e.target.value)}
                >
                  <option value="7 jours">7 jours</option>
                  <option value="15 jours">15 jours</option>
                  <option value="30 jours">30 jours</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group full-width">
                <label htmlFor="archivingPeriod">Durée d'archivage des PV</label>
                <select
                  id="archivingPeriod"
                  value={formData.archivingPeriod}
                  onChange={(e) => handleInputChange('archivingPeriod', e.target.value)}
                >
                  <option value="3 ans">3 ans</option>
                  <option value="5 ans">5 ans</option>
                  <option value="10 ans">10 ans</option>
                </select>
              </div>
            </div>
          </div>
        </div>
        <div className="group-btn">
          <Button
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveClick}
          >
            Enregistrer
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<PdfIcon />}
            onClick={handleGenClick}
          >
            Générer
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<VisibilityIcon />}
            onClick={handleShowPdf}
          >
            Afficher
          </Button>
          <Button
            variant="contained"
            className="btn-reset"
            onClick={fetchData}
          >
            Annuler
          </Button>
        </div>
      </div>

      <GenPdf
        file={file}
        isOpen={isOpen}
        closeDoc={() => setIsOpen(false)}
        scale={1}
      />
    </div>
  );
};

export default InternalRules;
